import React, { useState, useEffect } from "react";
import { Box, Typography, Card, CircularProgress, Tooltip, IconButton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import dayjs from "dayjs";
import styles from "./styles";

function inverteOperador(op) {
	if (op === ">") return "<";
	if (op === ">=") return "<=";
	if (op === "<") return ">";
	if (op === "<=") return ">=";
	return op;
}

function formatarMinutosSla(minutos) {
	if (minutos == null || isNaN(minutos) || minutos === "-") return "-";
	const totalMinutos = Math.round(minutos);
	if (totalMinutos < 0) return "-";
	return `${totalMinutos} min`;
}

// Helper for date formatting
function formatarDataHora(dateStr) {
	if (!dateStr) return "—";
	return dayjs(dateStr).format("DD/MM/YYYY HH:mm:ss");
}

// Bps Formatter
function formatarBps(v) {
	v = parseFloat(v) || 0;
	if (v === 0) return "0 bps";
	if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)} Gbps`;
	if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} Mbps`;
	if (v >= 1_000) return `${(v / 1_000).toFixed(2)} Kbps`;
	return `${v.toFixed(2)} bps`;
}

// Helper simples para labels do eixo Y dentro do SVG
function formatarBpsSimples(v) {
	v = parseFloat(v) || 0;
	if (v === 0) return "0 bps";
	if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Gbps`;
	if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} Mbps`;
	if (v >= 1_000) return `${(v / 1_000).toFixed(1)} Kbps`;
	return `${v.toFixed(1)} bps`;
}

// High-fidelity replica of montarSvgLinha from index.js
function montarSvgLinha(data = [], label = "", cor = "#4CAF50") {
	const W = 900,
		H = 200;

	const padLeft = 80,
		padRight = 20,
		padTop = 20,
		padBottom = 80;

	const chartW = W - padLeft - padRight;
	const chartH = H - padTop - padBottom;

	if (!data?.length) {
		return `<svg width="${W}" height="${H}" xmlns="http://www.w3.org/2000/svg">
      <rect width="${W}" height="${H}" fill="#ffffff" />
      <text x="${W / 2}" y="${H / 2}" text-anchor="middle" font-size="14" fill="#999">Sem dados</text>
    </svg>`;
	}

	const valores = data.map((d) => parseFloat(d?.value) || 0);
	const timestamps = data.map(
		(d) => new Date(d?.clock ? d.clock * 1000 : d?.time || 0),
	);
	const maxVal = Math.max(...valores, 1);
	const minTs = Math.min(...timestamps.map((t) => t.getTime()));
	const maxTs = Math.max(...timestamps.map((t) => t.getTime()));

	const pontos = valores
		.map((v, i) => {
			const x = padLeft + (i / Math.max(valores.length - 1, 1)) * chartW;
			const y = padTop + (1 - v / maxVal) * chartH;
			return `${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");

	const yTicks = [];
	for (let i = 0; i <= 4; i++) {
		const val = (maxVal * i) / 4;
		const yPos = padTop + chartH - (val / maxVal) * chartH;
		const labelY = formatarBpsSimples(val);
		yTicks.push(`
      <line x1="${padLeft - 5}" y1="${yPos.toFixed(1)}" x2="${padLeft + chartW}" y2="${yPos.toFixed(1)}"
            stroke="#e0e0e0" stroke-width="0.5" stroke-dasharray="3,3"/>
      <text x="${padLeft - 8}" y="${(yPos + 4).toFixed(1)}" text-anchor="end"
            font-size="11" fill="#666" font-family="sans-serif">${labelY}</text>
    `);
	}

	const xTicks = [];
	const numXTicks = 6;
	for (let i = 0; i <= numXTicks; i++) {
		const xPos = padLeft + (i / numXTicks) * chartW;
		const ts = new Date(minTs + (i / numXTicks) * (maxTs - minTs));

		const day = ts.getDate().toString().padStart(2, "0");
		const month = (ts.getMonth() + 1).toString().padStart(2, "0");
		const hours = ts.getHours().toString().padStart(2, "0");
		const minutes = ts.getMinutes().toString().padStart(2, "0");
		const lbl = `${day}:${month} ${hours}:${minutes}`;

		xTicks.push(`
      <line x1="${xPos.toFixed(1)}" y1="${padTop}" x2="${xPos.toFixed(1)}" y2="${(padTop + chartH).toFixed(1)}"
            stroke="#e0e0e0" stroke-width="0.5" stroke-dasharray="3,3"/>
      <line x1="${xPos.toFixed(1)}" y1="${(padTop + chartH).toFixed(1)}" x2="${xPos.toFixed(1)}" y2="${(padTop + chartH + 5).toFixed(1)}"
            stroke="#999" stroke-width="1"/>
      <text x="${xPos.toFixed(1)}" y="${(padTop + chartH + 20).toFixed(1)}" text-anchor="middle"
            font-size="10" fill="#666" font-family="sans-serif">${lbl}</text>
    `);
	}

	const maxLabelY = padTop + 6;

	return `<svg width="100%" height="100%" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
    <rect width="${W}" height="${H}" fill="#ffffff" />
    <line x1="${padLeft}" y1="${padTop}" x2="${padLeft + chartW}" y2="${padTop}"
          stroke="#999" stroke-width="0.8" stroke-dasharray="4,4"/>
    <text x="${padLeft + 2}" y="${maxLabelY + 2}" font-size="10" fill="#999" font-family="sans-serif">
      ${formatarBpsSimples(maxVal)}
    </text>
    ${yTicks.join("")}
    ${xTicks.join("")}
    <polyline points="${pontos}" fill="none" stroke="${cor}" stroke-width="1.5" stroke-linejoin="round"/>
    <line x1="${padLeft}" y1="${padTop}" x2="${padLeft}" y2="${padTop + chartH}"
          stroke="#999" stroke-width="1"/>
    <line x1="${padLeft}" y1="${padTop + chartH}" x2="${padLeft + chartW}" y2="${padTop + chartH}"
          stroke="#999" stroke-width="1"/>
  </svg>`;
}

// High-fidelity replica of montarSvgPizza from index.js
function montarSvgPizza(pct = 0) {
	const perc = Math.max(0, Math.min(100, pct));
	const angulo = (perc / 100) * 360;
	const rad = ((angulo - 90) * Math.PI) / 180;

	const cx = 80,
		cy = 50,
		r = 40;

	const x = cx + r * Math.cos(rad);
	const y = cy + r * Math.sin(rad);
	const largeArc = angulo > 180 ? 1 : 0;

	const corDisponivel = "#36ad86"; // Verde
	const corIndisponivel = "#d32f2f"; // Vermelho

	let path = "";
	if (angulo >= 360) {
		path = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${corDisponivel}"/>`;
	} else if (angulo > 0) {
		path = `<path d="M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${largeArc},1 ${x.toFixed(2)},${y.toFixed(2)} Z" fill="${corDisponivel}"/>`;
	}

	const anguloLinha = -Math.PI / 6;
	const x1 = (cx + r * Math.cos(anguloLinha)).toFixed(2);
	const y1 = (cy + r * Math.sin(anguloLinha)).toFixed(2);
	const x2 = (cx + (r + 12) * Math.cos(anguloLinha)).toFixed(2);
	const y2 = (cy + (r + 12) * Math.sin(anguloLinha)).toFixed(2);
	const x3 = 150;
	const y3 = y2;

	return `
    <svg width="100%" height="100%" viewBox="0 0 240 140" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="${corIndisponivel}"/>
        ${path}
        <polyline points="${x1},${y1} ${x2},${y2} ${x3},${y3}" fill="none" stroke="#888" stroke-width="1"/>
        <text x="${parseFloat(x3) + 4}" y="${parseFloat(y3) + 4}" font-family="Arial, sans-serif" font-size="11" font-weight="bold" fill="#333">
            ${perc.toFixed(1)}%
        </text>
        <rect x="25" y="115" width="10" height="10" rx="2" fill="${corDisponivel}"/>
        <text x="39" y="124" font-family="Arial, sans-serif" font-size="11" font-weight="500" fill="#666">
            Disponível
        </text>
        <rect x="110" y="115" width="10" height="10" rx="2" fill="${corIndisponivel}"/>
        <text x="124" y="124" font-family="Arial, sans-serif" font-size="11" font-weight="500" fill="#666">
            Indisponível
        </text>
    </svg>`.trim();
}

// Custom replica of pdfTable component styles in HTML
function PreviewTable({ columns = [], data = [] }) {
	if (!data || data.length === 0) return null;

	return (
		<table style={styles.tableStyles.container}>
			<thead>
				<tr style={{ backgroundColor: styles.tableStyles.headerCell.backgroundColor }}>
					{columns.map((col, index) => (
						<th
							key={index}
							style={{
								...styles.tableStyles.headerCell,
								textAlign: col.align || "left",
								borderRight: index === columns.length - 1 ? "none" : `1px solid ${styles.tableStyles.container.borderColor || "#bfbfbf"}`,
							}}
						>
							{col.header}
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{data.map((row, rowIndex) => {
					const isEven = rowIndex % 2 === 0;
					const bgColor = isEven ? styles.tableStyles.rowEven.backgroundColor : styles.tableStyles.rowOdd.backgroundColor;
					return (
						<tr key={rowIndex} style={{ backgroundColor: bgColor }}>
							{columns.map((col, colIndex) => {
								const val = col.render ? col.render(row) : row[col.dataKey];
								return (
									<td
										key={colIndex}
										style={{
											...styles.tableStyles.bodyCell,
											textAlign: col.align || "left",
											borderRight: colIndex === columns.length - 1 ? "none" : "1px solid #d8d8d8",
											borderBottom: rowIndex === data.length - 1 ? "none" : "1px solid #bfbfbf",
										}}
									>
										{val}
									</td>
								);
							})}
						</tr>
					);
				})}
			</tbody>
		</table>
	);
}

// Generate realistic simulated traffic data points
const generateMockTraffic = (startDate, endDate) => {
	const start = startDate ? new Date(startDate) : new Date(Date.now() - 7 * 24 * 3600 * 1000);
	const end = endDate ? new Date(endDate) : new Date();
	const diff = end.getTime() - start.getTime();

	const mockData = [];
	const pointsCount = 45; // Smooth chart curve points
	for (let i = 0; i < pointsCount; i++) {
		const time = new Date(start.getTime() + (i / (pointsCount - 1)) * diff);
		// Sine wave + random noise to create a beautiful bandwidth-like graph
		const base = 45_000_000 + 35_000_000 * Math.sin((i / pointsCount) * Math.PI * 4);
		const noise = (Math.random() - 0.5) * 15_000_000;
		const val = Math.max(0, base + noise);

		mockData.push({
			clock: Math.floor(time.getTime() / 1000),
			value: val,
		});
	}
	return mockData;
};

export default function ReportPreview({ states = {}, clienteInfo = null, theme = {}, api = null }) {
	const [generalSetting, setGeneralSetting] = useState(null);
	const [loadingSettings, setLoadingSettings] = useState(false);

	// Extract intercepted states
	const {
		selectedTypes = [],
		nomeRelatorio = "",
		dataInicial = null,
		dataFinal = null,
		metrics = {},
		tarifacaoValores = {},
		observacoes = "",
		periodoTipo = "Semanal",
		isAgendamentoEnabled = false,
		rules = [],
	} = states ?? {};

	const To = Math.round((new Date(dataFinal || Date.now()).getTime() - new Date(dataInicial || Date.now()).getTime()) / (1000 * 60)) || 10080;

	const nomeCliente = clienteInfo?.nome || "Cliente";

	// Fetch general settings on mount
	useEffect(() => {
		if (api) {
			setLoadingSettings(true);
			api.post("/interno/find/generalSetting", { product: "usageReport" })
				.then((res) => {
					setGeneralSetting(res?.data || null);
				})
				.catch((err) => {
					console.error("Error loading general settings:", err);
				})
				.finally(() => {
					setLoadingSettings(false);
				});
		}
	}, [api]);

	// Generate mock data for dates
	const mockTrafficData = React.useMemo(() => {
		return generateMockTraffic(dataInicial, dataFinal);
	}, [dataInicial, dataFinal]);

	// Compute traffic metric summary
	const computedMetrics = React.useMemo(() => {
		const values = mockTrafficData.map((d) => d.value);
		const min = Math.min(...values);
		const max = Math.max(...values);
		const avg = values.reduce((a, b) => a + b, 0) / values.length;
		// 95th percentile
		const sorted = [...values].sort((a, b) => a - b);
		const percentileIdx = Math.floor(sorted.length * 0.95);
		const percentile = sorted[percentileIdx];

		return { min, max, avg, percentile };
	}, [mockTrafficData]);

	// Compute availability minutes
	const availabilityInfo = React.useMemo(() => {
		if (!dataInicial || !dataFinal) return null;
		const inicio = new Date(dataInicial).getTime();
		const fim = new Date(dataFinal).getTime();
		const To = Math.round((fim - inicio) / (1000 * 60));
		if (To <= 0) return null;

		const perc = 99.98 / 100;
		const Ti = Math.round(To * (1 - perc));

		return {
			To: To.toLocaleString("pt-BR"),
			Ti: Ti.toLocaleString("pt-BR"),
			D: "99.98",
		};
	}, [dataInicial, dataFinal]);

	// Format dates
	const dataIniFormatada = isAgendamentoEnabled ? "Agendado" : formatarDataHora(dataInicial);
	const dataFimFormatada = isAgendamentoEnabled ? "Agendado" : formatarDataHora(dataFinal);

	// Generate Line Chart SVG
	const lineChartSvg = React.useMemo(() => {
		const strokeColor = theme?.palette?.cardGraphic?.graphLineColor?.green || "#2e7d32";
		return montarSvgLinha(mockTrafficData, "Tráfego (bps)", strokeColor);
	}, [mockTrafficData, theme]);

	// Generate Availability Pizza SVG
	const pizzaChartSvg = React.useMemo(() => {
		return montarSvgPizza(99.98);
	}, []);

	return (
		<Box sx={{ position: "sticky", top: "140px", width: "100%" }}>
			
			{/* Desk Background "Paper" effect */}
			<Box
				sx={{
					width: "100%",
					backgroundColor: theme?.palette?.background?.paper,
					display: "flex",
					justifyContent: "center",
					flexDirection: "column",
					alignItems: "flex-start",
					padding: "20px",
					borderRadius: "16px",
					minHeight: "600px",
					overflow: "auto",
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.secondary" }}>
					Pré-visualização
				</Typography>
				<Tooltip title="Esta é uma simulação visual do relatório final (primeira página). Os dados e gráficos apresentados são fictícios e servem apenas para demonstrar a estrutura do layout." arrow>
					<IconButton size="small" sx={{ p: 0.2, color: theme.palette.primary.main }}>
						<InfoOutlined fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>

				{/* Mock of A4 PDF Page - STRICT SIZE - NEVER OVERFLOWS PAGE 1 */}
				<Card
					sx={{
						width: "100%",
						aspectRatio: "210 / 297", // Exact A4 proportion from HeaderReport
						bgcolor: "#ffffff",
						color: "#333333",
						p: "30px", // Exact padding from HeaderReport
						boxShadow: "0px 4px 20px rgba(0,0,0,0.15)", // Exact shadow from HeaderReport
						position: "relative",
						overflow: "hidden", // Never show internal scrollbars on page 1, clip overflowing contents
						display: "flex",
						flexDirection: "column",
						borderRadius: "0px", // A4 sheet sharp corners
						boxSizing: "border-box",
					}}
				>
					{/* DIAGONAL WATERMARK "Meu Relatório" */}
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%) rotate(-45deg)", // Exact rotation from HeaderReport
							fontSize: { xs: "70px" }, // Scaled slightly to fit perfectly on A4 card
							fontWeight: 600, // Exact weight from HeaderReport
							color: "rgba(226, 21, 21, 0.3)", // Faint watermark color
							
							textTransform: "uppercase",
							letterSpacing: "4px",
							pointerEvents: "none",
							zIndex: 10,
							whiteSpace: "nowrap",
							userSelect: "none",
						}}
					>
						Meu Relatório
					</Box>

					{loadingSettings ? (
						<Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
							<CircularProgress size={30} />
						</Box>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", height: "100%", zIndex: 2 }}>
							{/* HEADER */}
							<Box sx={{ display: "flex", justifyContent: "space-between", mb: "8px" }}>
								{/* Left: Logo or Reason Social */}
								<Box sx={{ width: "110px", display: "flex", alignItems: "center" }}>
									{generalSetting?.imagem ? (
										<img
											src={generalSetting.imagem}
											alt="Logo"
											style={{ maxWidth: "100%", maxHeight: "40px", objectFit: "contain" }}
										/>
									) : (
										<Typography sx={styles.pdfStyles.nomeEmpresa}>
											{generalSetting?.razaoSocial || ""}
										</Typography>
									)}
								</Box>

								{/* Right: Company details */}
								<Box sx={{ textAlign: "right", display: "flex", flexDirection: "column" }}>
									<Typography sx={styles.pdfStyles.nomeEmpresa}>
										{generalSetting?.razaoSocial || ""}
									</Typography>
									<Typography sx={styles.pdfStyles.dadosEmpresa}>
										{generalSetting?.cnpj ? `CNPJ: ${generalSetting.cnpj}` : ""}
									</Typography>
									<Typography sx={styles.pdfStyles.dadosEmpresa}>
										{generalSetting?.endereco || ""}
									</Typography>
								</Box>
							</Box>

							{/* DIVIDER LINE */}
							<hr style={{ border: "none", borderTop: "1px solid #2c2b2b", margin: "0 0 12px 0" }} />

							{/* REPORT PERIOD & METADATA */}
							<Box sx={{ mb: "10px" }}>
								<Typography sx={styles.pdfStyles.tituloPeriodo}>
									PERÍODO DO RELATÓRIO
								</Typography>
								<Typography sx={{ ...styles.pdfStyles.campo, mb: "2px" }}>
									Data Inicial: {dataIniFormatada}
								</Typography>
								<Typography sx={{ ...styles.pdfStyles.campo, mb: "6px" }}>
									Data Final: {dataFimFormatada}
								</Typography>
								<Typography sx={{ ...styles.pdfStyles.tituloCliente, m: 0 }}>
									Cliente : {nomeCliente}
								</Typography>
							</Box>

							{/* SECTIONS CONTENT */}
							{selectedTypes.length === 0 ? (
								<Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center", border: "1px dashed #cccccc", borderRadius: "4px", p: 4, mt: 2 }}>
									<Typography sx={{ fontSize: "11px", color: "#888888", textAlign: "center" }}>
										Nenhum tipo de relatório selecionado.<br />
										Selecione "CPE", "ONU" ou "Atendimentos" no formulário para pré-visualizar.
									</Typography>
								</Box>
							) : (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
									{/* CPE SECTION */}
									{selectedTypes.includes("CPE") && (
										<Box sx={{ borderTop: "1px solid #e0e0e0", pt: 1 }}>
											{/* Intro text */}
											{(metrics?.trafego || metrics?.tarifacao) && (
												<>
													<Typography sx={styles.pdfStyles.textoIntro}>
														Resultados de tráfego relacionados ao cliente {nomeCliente}. As métricas utilizadas para os cálculos dos valores apresentados são: tráfego total, 95th (percentil). As coletas dos valores apresentados neste relatório correspondem ao período entre {formatarDataHora(dataInicial)} e {formatarDataHora(dataFinal)}.
													</Typography>

													{/* SVG LINE CHART WITH BLUR */}
													<Box sx={{ mb: 1.5, textAlign: "center" }}>
														<Typography sx={styles.pdfStyles.tituloGrafico}>
															Gráfico 1: percentil do cliente {nomeCliente}
														</Typography>
														<Box 
															sx={styles.svgContainer} 
															dangerouslySetInnerHTML={{ __html: lineChartSvg }}
														/>
														<Typography sx={styles.pdfStyles.fonte}>
															Dados coletados pelo Monitoramento {generalSetting?.razaoSocial || ""}
														</Typography>
													</Box>

													{/* CONSUMPTION TABLE */}
													<PreviewTable 
														columns={[
															{ header: "Descrição", dataKey: "descricao" },
															{ header: "Valor", dataKey: "valor", align: "right" }
														]}
														data={[
															{ descricao: "Menor Consumo", valor: formatarBps(computedMetrics?.min) },
															{ descricao: "Consumo Médio", valor: formatarBps(computedMetrics?.avg) },
															{ descricao: "Maior Consumo", valor: formatarBps(computedMetrics?.max) },
															{ descricao: "Percentil 95th", valor: formatarBps(computedMetrics?.percentile) }
														]}
													/>
												</>
											)}

											{/* TARIFACAO TABLE */}
											{metrics?.tarifacao && (
												<Box sx={{ mt: 0.5 }}>
													<Typography sx={styles.pdfStyles.subtituloSecao}>
														Tarifação 95th
													</Typography>
													<PreviewTable 
														columns={[
															{ header: "Descrição", dataKey: "descricao" },
															{ header: "Valor", dataKey: "valor", align: "right" }
														]}
														data={[
															{ descricao: "Plano contratado (Mbps)", valor: `${tarifacaoValores?.planoContratadoMbps || "0"} Mbps` },
															{ descricao: "Valor por Mbps (R$)", valor: `R$ ${Number(tarifacaoValores?.valorPorMbps || 0).toFixed(2)}` },
															{ descricao: "Valor excedente (R$/Mbps)", valor: `R$ ${Number(tarifacaoValores?.valorMbpsExcedente || 0).toFixed(2)}` }
														]}
													/>
												</Box>
											)}

											{/* QUALITY SLA METRICS */}
											{(metrics?.disponibilidade || metrics?.perdaPacote || metrics?.latencia) && (
												<Box sx={{ mt: 0.5 }}>
													<Typography sx={{ ...styles.pdfStyles.campo, color: "#888888", mb: 1 }}>
														Período: {formatarDataHora(dataInicial)} à {formatarDataHora(dataFinal)}
													</Typography>

													{metrics?.disponibilidade && (
														<Box sx={{ mb: 1.5, textAlign: "center" }}>
															<Typography sx={styles.pdfStyles.tituloGrafico}>
																Gráfico {metrics?.trafego || metrics?.tarifacao ? 2 : 1}: Disponibilidade do Equipamento
															</Typography>
															<Box 
																sx={styles.pieSvgContainer} 
																dangerouslySetInnerHTML={{ __html: pizzaChartSvg }}
															/>
															<Typography sx={styles.pdfStyles.fonte}>
																Dados coletados pelo Monitoramento {generalSetting?.razaoSocial || ""}
															</Typography>
															<Typography sx={styles.pdfStyles.textoIntro}>
																O gráfico de pizza representa a porcentagem de tempo em que os equipamentos monitorados estiveram disponíveis ou indisponíveis durante o período analisado, com base nas respostas de testes de conectividade (ICMP ping).
															</Typography>
														</Box>
													)}

													{rules?.length > 0 && (
														<PreviewTable 
															columns={[
																{ header: "Métrica", dataKey: "metrica" },
																{ 
																	header: "Resultados no Período", 
																	dataKey: "resultado", 
																	align: "center",
																	render: (row) => <span style={{ color: row.cor }}>{row.resultado}</span>
																},
																{ header: "SLA Esperado", dataKey: "valorSLA", align: "center" },
																{ header: "Tempo Total", dataKey: "tempoTotal", align: "center" },
																{ header: "Tempo Referência", dataKey: "tempoRef", align: "center" },
																{ 
																	header: "Excedente", 
																	dataKey: "excedente", 
																	align: "center",
																	render: (row) => <span style={{ color: row.excedenteCor }}>{row.excedente}</span>
																}
															]}
															data={[
																...(metrics?.disponibilidade ? (() => {
																	const rule = rules?.find?.((r) => r?.metric === "disponibilidade");
																	const slaPct = parseFloat(rule?.value || "95.00");
																	const pct = 99.98;
																	const uptimeActualMins = To * (pct / 100);
																	const uptimeExpectedMins = To * (slaPct / 100);
																	const uptimeDiff = uptimeActualMins - uptimeExpectedMins;
																	const uptimeExcedenteVal = uptimeDiff >= 0 
																		? `+${formatarMinutosSla(uptimeDiff)}` 
																		: `-${formatarMinutosSla(Math.abs(uptimeDiff))}`;
																	const uptimeExcedenteCor = uptimeDiff >= 0 ? "#2e7d32" : "#d32f2f";

																	const downtimePct = 100 - pct;
																	const allowedDowntimePct = 100 - slaPct;
																	const downtimeActualMins = To * (downtimePct / 100);
																	const downtimeAllowedMins = To * (allowedDowntimePct / 100);
																	const downtimeDiff = downtimeActualMins - downtimeAllowedMins;
																	const downtimeExcedenteVal = downtimeDiff > 0 
																		? `+${formatarMinutosSla(downtimeDiff)}` 
																		: `${formatarMinutosSla(downtimeDiff)}`;
																	const downtimeExcedenteCor = downtimeDiff > 0 ? "#d32f2f" : "#2e7d32";

																	return [
																		{
																			metrica: "Disponibilidade de Coleta de Ping",
																			resultado: "99.98 %",
																			valorSLA: `${rule?.operator ?? ">"} ${rule?.value || "97.50"} %`,
																			tempoTotal: formatarMinutosSla(uptimeActualMins),
																			tempoRef: formatarMinutosSla(uptimeExpectedMins),
																			excedente: uptimeExcedenteVal,
																			excedenteCor: uptimeExcedenteCor,
																			cor: "#2e7d32"
																		},
																		{
																			metrica: "Indisponibilidade de Coleta de Ping",
																			resultado: `${downtimePct.toFixed(2)} %`,
																			valorSLA: `${inverteOperador(rule?.operator ?? ">")} ${allowedDowntimePct.toFixed(2)} %`,
																			tempoTotal: formatarMinutosSla(downtimeActualMins),
																			tempoRef: formatarMinutosSla(downtimeAllowedMins),
																			excedente: downtimeExcedenteVal,
																			excedenteCor: downtimeExcedenteCor,
																			cor: "#2e7d32"
																		}
																	];
																})() : []),
																...(metrics?.latencia ? [{
																	metrica: "Latência Média (ms)",
																	resultado: "— ms",
																	valorSLA: (() => {
																		const rule = rules?.find?.((r) => r?.metric === "latencia");
																		return `${rule?.operator ?? "<"} ${rule?.value || "50"} ms`;
																	})(),
																	tempoTotal: "-",
																	tempoRef: "-",
																	excedente: "-",
																	cor: "#2e7d32"
																}] : []),
																...(metrics?.perdaPacote ? [{
																	metrica: "Perda de Pacotes (%)",
																	resultado: "— %",
																	valorSLA: (() => {
																		const rule = rules?.find?.((r) => r?.metric === "packetLoss");
																		return `${rule?.operator ?? "<"} ${rule?.value || "1.00"} %`;
																	})(),
																	tempoTotal: "-",
																	tempoRef: "-",
																	excedente: "-",
																	cor: "#2e7d32"
																}] : [])
															]}
														/>
													)}

													{/* Availability calculation memory */}
													{metrics?.disponibilidade && availabilityInfo && (
														<Box sx={{ mt: 0.5 }}>
															<Typography sx={styles.pdfStyles.subtituloSecao}>
																Cálculo de Disponibilidade
															</Typography>
															<Typography sx={styles.pdfStyles.textoIntro}>
																A fórmula abaixo detalha os parâmetros utilizados para atingir o percentual de disponibilidade apresentado:
															</Typography>
															<Box sx={{ pl: 1, mb: 1.5 }}>
																<Typography sx={{ ...styles.pdfStyles.campo, fontSize: "8px", whiteSpace: "pre-line" }}>
																	<strong>Fórmula:</strong> D = [(To - Ti) / To] × 100{"\n"}
																	<strong>Onde:</strong>{"\n"}
																	• D = disponibilidade do serviço, To = tempo total, Ti = tempo indisponível{"\n"}
																	<strong>Valores Apurados:</strong>{"\n"}
																	• To = {availabilityInfo?.To} min | Ti = {availabilityInfo?.Ti} min{"\n"}
																	<strong>Cálculo:</strong> D = [({availabilityInfo?.To} - {availabilityInfo?.Ti}) / {availabilityInfo?.To}] × 100 = <strong>{availabilityInfo?.D}%</strong>
																</Typography>
															</Box>
														</Box>
													)}
												</Box>
											)}
										</Box>
									)}

									{/* ONU SECTION - FULL FIDELITY TO montarPdf.js */}
									{selectedTypes.includes("ONU") && (
										<Box sx={{ borderTop: "1px solid #e0e0e0", pt: 1 }}>
											<Typography sx={styles.pdfStyles.subtituloSecao}>
												ONU
											</Typography>
											<Typography sx={styles.pdfStyles.textoIntro}>
												Este relatório apresenta o acompanhamento dos indicadores de desempenho (SLA) dos equipamentos ONU monitorados no período de {formatarDataHora(dataInicial)} a {formatarDataHora(dataFinal)}.
											</Typography>
											<Typography sx={styles.pdfStyles.textoIntro}>
												Foram analisadas as métricas de Potência Recebida (dBm) e Disponibilidade, com base nos dados coletados automaticamente pelos sistemas de monitoramento.
											</Typography>
											<Typography sx={styles.pdfStyles.textoIntro}>
												O objetivo é avaliar a qualidade do enlace óptico e a estabilidade operacional dos equipamentos, assegurando a conformidade com os níveis de serviço estabelecidos.
											</Typography>

											{/* Mock item loops with graphics and tables */}
											<Box sx={{ mt: 1 }}>
												<Typography sx={{ ...styles.pdfStyles.campo, fontWeight: "bold", mb: 1 }}>
													Equipamento: ONU_Cliente_Demo (PPPoE / Serial)
												</Typography>

												{/* RX POWER CHART */}
												<Box sx={{ mb: 1.5, textAlign: "center" }}>
													<Typography sx={styles.pdfStyles.tituloGrafico}>
														Gráfico 1: Potência Recebida (dBm)
													</Typography>
													<Box 
														sx={styles.svgContainer} 
														dangerouslySetInnerHTML={{ __html: lineChartSvg }}
													/>
													<Typography sx={styles.pdfStyles.fonte}>
														Dados coletados pelo Monitoramento {generalSetting?.razaoSocial || ""}
													</Typography>
													<Typography sx={styles.pdfStyles.textoIntro}>
														O gráfico acima demonstra a variação da potência óptica recebida (em dBm) ao longo do período analisado. Esses valores representam a qualidade do sinal entregue ao equipamento, permitindo identificar padrões de degradação que possam impactar a estabilidade do serviço.
													</Typography>
												</Box>

												{/* AVAILABILITY PIE CHART */}
												<Box sx={{ mb: 1.5, textAlign: "center" }}>
													<Typography sx={styles.pdfStyles.tituloGrafico}>
														Gráfico 2: Disponibilidade da ONU
													</Typography>
													<Box 
														sx={styles.pieSvgContainer} 
														dangerouslySetInnerHTML={{ __html: pizzaChartSvg }}
													/>
													<Typography sx={styles.pdfStyles.fonte}>
														Dados coletados pelo Monitoramento {generalSetting?.razaoSocial || ""}
													</Typography>
													<Typography sx={styles.pdfStyles.textoIntro}>
														Este gráfico apresenta o percentual de tempo de disponibilidade dos equipamentos ONU durante o período do relatório. O gráfico demonstra o percentual de tempo em que a ONU permaneceu dentro dos parâmetros aceitáveis de operação, refletindo a estabilidade geral do serviço.
													</Typography>
												</Box>

												{/* SLA TABLE */}
												{rules?.length > 0 && (
													<PreviewTable 
														columns={[
															{ header: "Métrica", dataKey: "metrica" },
															{ 
																header: "Resultados no Período", 
																dataKey: "resultado", 
																align: "center",
																render: (row) => <span style={{ color: row.cor }}>{row.resultado}</span>
															},
															{ header: "SLA Esperado", dataKey: "sla", align: "center" },
															{ header: "Tempo Total", dataKey: "tempoTotal", align: "center" },
															{ header: "Tempo Referência", dataKey: "tempoRef", align: "center" },
															{ 
																header: "Excedente", 
																dataKey: "excedente", 
																align: "center",
																render: (row) => <span style={{ color: row.excedenteCor }}>{row.excedente}</span>
															}
														]}
														data={(() => {
															const ruleDisp = rules?.find?.((r) => r?.metric === "disponibilidade");
															const rulePotencia = rules?.find?.((r) => r?.metric === "potencia");
															const operatorDisp = ruleDisp?.operator || ">";
															const valorRegraDisp = ruleDisp?.value || "97.5";
															const operatorPotencia = rulePotencia?.operator || ">";
															const valorRegraPotencia = rulePotencia?.value || "-28";

															const pctDisp = 99.85;
															const slaPctDisp = parseFloat(valorRegraDisp);

															const uptimeActualMins = To * (pctDisp / 100);
															const uptimeExpectedMins = To * (slaPctDisp / 100);
															const uptimeDiff = uptimeActualMins - uptimeExpectedMins;
															const uptimeExcedenteVal = uptimeDiff >= 0 
																? `+${formatarMinutosSla(uptimeDiff)}` 
																: `-${formatarMinutosSla(Math.abs(uptimeDiff))}`;
															const uptimeExcedenteCor = uptimeDiff >= 0 ? "#2e7d32" : "#d32f2f";

															const pctIndisp = 100 - pctDisp;
															const allowedDowntimePct = 100 - slaPctDisp;
															const downtimeActualMins = To * (pctIndisp / 100);
															const downtimeAllowedMins = To * (allowedDowntimePct / 100);
															const downtimeDiff = downtimeActualMins - downtimeAllowedMins;
															const downtimeExcedenteVal = downtimeDiff > 0 
																? `+${formatarMinutosSla(downtimeDiff)}` 
																: `${formatarMinutosSla(downtimeDiff)}`;
															const downtimeExcedenteCor = downtimeDiff > 0 ? "#d32f2f" : "#2e7d32";

															return [
																{
																	metrica: "Potência Média Recebida (dBm)",
																	resultado: "-22.45 dBm",
																	sla: `${operatorPotencia} ${valorRegraPotencia} dBm`,
																	tempoTotal: "-",
																	tempoRef: "-",
																	excedente: "-",
																	cor: "#2e7d32"
																},
																{
																	metrica: "Disponibilidade (%)",
																	resultado: `${pctDisp.toFixed(2)}%`,
																	sla: `${operatorDisp} ${valorRegraDisp}%`,
																	tempoTotal: formatarMinutosSla(uptimeActualMins),
																	tempoRef: formatarMinutosSla(uptimeExpectedMins),
																	excedente: uptimeExcedenteVal,
																	excedenteCor: uptimeExcedenteCor,
																	cor: "#2e7d32"
																},
																{
																	metrica: "Indisponibilidade (%)",
																	resultado: `${pctIndisp.toFixed(2)}%`,
																	sla: `${inverteOperador(operatorDisp)} ${allowedDowntimePct.toFixed(2)}%`,
																	tempoTotal: formatarMinutosSla(downtimeActualMins),
																	tempoRef: formatarMinutosSla(downtimeAllowedMins),
																	excedente: downtimeExcedenteVal,
																	excedenteCor: downtimeExcedenteCor,
																	cor: "#2e7d32"
																}
															];
														})()}
													/>
												)}

												{/* Availability calculation memory */}
												{availabilityInfo && (
													<Box sx={{ mt: 0.5 }}>
														<Typography sx={styles.pdfStyles.subtituloSecao}>
															Cálculo de Disponibilidade
														</Typography>
														<Typography sx={styles.pdfStyles.textoIntro}>
															A fórmula abaixo detalha os parâmetros utilizados para atingir o percentual de disponibilidade apresentado:
														</Typography>
														<Box sx={{ pl: 1, mb: 1.5 }}>
															<Typography sx={{ ...styles.pdfStyles.campo, fontSize: "8px", whiteSpace: "pre-line" }}>
																<strong>Fórmula:</strong> D = [(To - Ti) / To] × 100{"\n"}
																<strong>Valores Apurados:</strong> To = {availabilityInfo?.To} min | Ti = {availabilityInfo?.Ti} min{"\n"}
																<strong>Cálculo:</strong> D = [({availabilityInfo?.To} - {availabilityInfo?.Ti}) / {availabilityInfo?.To}] × 100 = <strong>99.85%</strong>
															</Typography>
														</Box>
													</Box>
												)}
											</Box>
										</Box>
									)}

									{/* ATENDIMENTOS SECTION - FULL FIDELITY TO montarPdf.js */}
									{selectedTypes.includes("Atendimentos") && (
										<Box sx={{ borderTop: "1px solid #e0e0e0", pt: 1 }}>
											<Typography sx={{ ...styles.pdfStyles.subtituloSecao, mb: 1 }}>
												Atendimentos 
											</Typography>
											<Typography sx={{ ...styles.pdfStyles.campo, mb: 1 }}>
												Total de tickets com RFO: 1
											</Typography>

											<Box sx={{ mt: 1 }}>
												<Typography sx={{ ...styles.pdfStyles.campo, fontWeight: "bold", mb: 0.5 }}>
													Internet Fibra (Serviço)
												</Typography>
												<PreviewTable 
													columns={[
														{ header: "Protocolo", dataKey: "protocolo" },
														{ header: "Status", dataKey: "status" },
														{ header: "Abertura", dataKey: "abertura" },
														{ header: "Fechamento", dataKey: "fechamento" }
													]}
													data={[
														{
															protocolo: "20260601001",
															status: "Concluído",
															abertura: dataIniFormatada,
															fechamento: dataFimFormatada
														}
													]}
												/>
											</Box>
										</Box>
									)}

									{/* OBSERVATIONS SECTION */}
									{observacoes && (
										<Box sx={{ borderTop: "1px solid #e0e0e0", pt: 1 }}>
											<Typography sx={{ ...styles.pdfStyles.subtituloSecao, mb: 0.5 }}>
												Observações:
											</Typography>
											<Typography sx={{ ...styles.pdfStyles.campo, textAlign: "justify" }}>
												{observacoes}
											</Typography>
										</Box>
									)}
								</Box>
							)}
						</Box>
					)}
				</Card>
			</Box>
		</Box>
	);
}
